// src/utils/settings.ts
/**
 * LocalStorage keys for user-configurable settings.
 */
export const X_REWRITE_STORAGE_KEY = 'reddit_rewrite_x_links';
export const SHOW_SELFTEXT_PREVIEW_KEY = 'reddit_show_selftext_preview';
export const NSFW_CONSENT_SESSION_KEY_PREFIX = 'nsfw_consent_';
export const GEMINI_API_KEY_STORAGE_KEY = 'reddit_gemini_api_key'; // New key

/**
 * Hardcoded setting to completely block NSFW content.
 * Set to `true` to block, `false` to allow (with warnings).
 */
export const BLOCK_NSFW: boolean = false; // <<< Set to true to block all NSFW


/**
 * Gets the current value of the 'Rewrite X Links' setting from localStorage.
 * Defaults to false if the setting is not found or invalid.
 * @returns {boolean} True if the setting is enabled, false otherwise.
 */
export const getRewriteXLinksSetting = (): boolean => {
    try {
        const storedValue = localStorage.getItem(X_REWRITE_STORAGE_KEY);
        // Check explicitly for 'true' string, default to false otherwise
        return storedValue === 'true';
    } catch (error) {
        console.error("Error reading rewrite setting from localStorage:", error);
        return false; // Default to false on error
    }
};

/**
 * Sets the value of the 'Rewrite X Links' setting in localStorage.
 * Stores the boolean value as a string ('true' or 'false').
 * @param {boolean} enabled - The desired state of the setting.
 */
export const setRewriteXLinksSetting = (enabled: boolean): void => {
    try {
        localStorage.setItem(X_REWRITE_STORAGE_KEY, enabled ? 'true' : 'false');
    } catch (error) {
        console.error("Error saving rewrite setting to localStorage:", error);
        // Optionally notify the user or handle the error
    }
};

/**
 * Gets the current value of the 'Show Selftext Preview' setting from localStorage.
 * Defaults to true if the setting is not found or invalid.
 * @returns {boolean} True if the setting is enabled, false otherwise.
 */
export const getShowSelftextPreviewSetting = (): boolean => {
    try {
        const storedValue = localStorage.getItem(SHOW_SELFTEXT_PREVIEW_KEY);
        // Default to 'true' if not set or invalid
        return storedValue !== 'false';
    } catch (error) {
        console.error("Error reading selftext preview setting from localStorage:", error);
        return true; // Default to true on error
    }
};

/**
 * Sets the value of the 'Show Selftext Preview' setting in localStorage.
 * Stores the boolean value as a string ('true' or 'false').
 * @param {boolean} enabled - The desired state of the setting.
 */
export const setShowSelftextPreviewSetting = (enabled: boolean): void => {
    try {
        localStorage.setItem(SHOW_SELFTEXT_PREVIEW_KEY, enabled ? 'true' : 'false');
    } catch (error) {
        console.error("Error saving selftext preview setting to localStorage:", error);
    }
};


/**
 * Checks sessionStorage if the user has consented to view a specific NSFW item during this session.
 * @param itemId Unique identifier for the NSFW item (e.g., `subreddit_reactjs`, `post_abcde`)
 * @returns True if consent has been given for this item in this session, false otherwise.
 */
export const getSessionNsfwConsent = (itemId: string): boolean => {
    try {
        const key = `${NSFW_CONSENT_SESSION_KEY_PREFIX}${itemId}`;
        return sessionStorage.getItem(key) === 'true';
    } catch (error) {
        console.error("Error reading NSFW consent from sessionStorage:", error);
        return false;
    }
};

/**
 * Stores consent for viewing a specific NSFW item in sessionStorage for the current session.
 * @param itemId Unique identifier for the NSFW item (e.g., `subreddit_reactjs`, `post_abcde`)
 */
export const setSessionNsfwConsent = (itemId: string): void => {
     try {
        const key = `${NSFW_CONSENT_SESSION_KEY_PREFIX}${itemId}`;
        sessionStorage.setItem(key, 'true');
    } catch (error) {
        console.error("Error saving NSFW consent to sessionStorage:", error);
    }
};

// --- Gemini API Key ---

/**
 * Gets the saved Gemini API Key from localStorage.
 * @returns {string | null} The API key string, or null if not set.
 */
export const getGeminiApiKeySetting = (): string | null => {
    try {
        return localStorage.getItem(GEMINI_API_KEY_STORAGE_KEY);
    } catch (error) {
        console.error("Error reading Gemini API Key from localStorage:", error);
        return null;
    }
};

/**
 * Sets the Gemini API Key in localStorage.
 * Removes the key if the provided key is null or an empty string.
 * @param {string | null} apiKey - The API key to save, or null/empty to clear.
 */
export const setGeminiApiKeySetting = (apiKey: string | null): void => {
    try {
        if (apiKey && apiKey.trim() !== '') {
            localStorage.setItem(GEMINI_API_KEY_STORAGE_KEY, apiKey.trim());
        } else {
            localStorage.removeItem(GEMINI_API_KEY_STORAGE_KEY);
        }
    } catch (error) {
        console.error("Error saving Gemini API Key to localStorage:", error);
    }
};